import React, { useState, useEffect } from 'react';
import './DebugPanel.css';

interface DebugPanelProps {
  devices: any[];
  apiResponses: any[];
  isExpanded?: boolean;
}

const DebugPanel: React.FC<DebugPanelProps> = ({ 
  devices, 
  apiResponses,
  isExpanded = false
}) => {
  const [expanded, setExpanded] = useState<boolean>(isExpanded);
  const [activeTab, setActiveTab] = useState<'devices' | 'api' | 'logs'>('devices');
  const [logs, setLogs] = useState<string[]>([]);

  // Capture console logs
  useEffect(() => {
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    
    const captureLog = (type: string, ...args: any[]) => {
      const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      setLogs(prevLogs => {
        const newLogs = [...prevLogs, `[${timestamp}] [${type}] ${message}`];
        // Keep only the last 100 logs
        return newLogs.slice(-100);
      });
    };
    
    console.log = (...args) => {
      originalConsoleLog(...args);
      captureLog('INFO', ...args);
    };
    
    console.error = (...args) => {
      originalConsoleError(...args);
      captureLog('ERROR', ...args);
    };
    
    console.warn = (...args) => {
      originalConsoleWarn(...args);
      captureLog('WARN', ...args);
    };
    
    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  return (
    <div className={`debug-panel ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="debug-panel-header">
        <h3>Debug Panel</h3>
        <button 
          className="toggle-button"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>
      
      {expanded && (
        <>
          <div className="debug-panel-tabs">
            <button 
              className={`tab-button ${activeTab === 'devices' ? 'active' : ''}`}
              onClick={() => setActiveTab('devices')}
            >
              Devices ({devices.length})
            </button>
            <button 
              className={`tab-button ${activeTab === 'api' ? 'active' : ''}`}
              onClick={() => setActiveTab('api')}
            >
              API Responses ({apiResponses.length})
            </button>
            <button 
              className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Logs ({logs.length})
            </button>
          </div>
          
          <div className="debug-panel-content">
            {activeTab === 'devices' && (
              <div className="devices-debug">
                <pre>{JSON.stringify(devices, null, 2)}</pre>
              </div>
            )}
            
            {activeTab === 'api' && (
              <div className="api-debug">
                {apiResponses.length > 0 ? (
                  <pre>{JSON.stringify(apiResponses, null, 2)}</pre>
                ) : (
                  <p>No API responses recorded yet.</p>
                )}
              </div>
            )}
            
            {activeTab === 'logs' && (
              <div className="logs-debug">
                {logs.map((log, index) => (
                  <div key={index} className="log-entry">
                    {log}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DebugPanel;